// dashboard/patientList/ConditionRow.js
// One row per condition inside a PetCard. Extracted from App.js
// (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { c, severityColor, severityLabel, conditionLabel, onActivateKeyDown } from '../constants';
import { relDate } from '../utils';
import StatusNarrative from './StatusNarrative';

export default function ConditionRow({ row, onSelect, onDismissAlert, onReactivateAlert, isSelected, isInCatchUpWindow, recoveryThresholds }) {
  const { t } = useTranslation();
  const sev   = row.severity;
  const color = severityColor(sev);
  // #9: threshold for THIS alert's current severity tier — recoveryThresholds
  // is keyed by condition_type, each value holding all three tiers, since a
  // condition can carry different thresholds per tier (e.g. a stricter
  // immediate_review count than yellow).
  const recoveryThreshold = recoveryThresholds?.[row.condition?.condition_type]?.[sev];
  return (
    <div
      onClick={() => onSelect(row)}
      onKeyDown={onActivateKeyDown(() => onSelect(row))}
      tabIndex={0}
      role="button"
      aria-label={t('patientCard.conditionRowAria', { condition: conditionLabel(row.condition?.condition_type), severity: severityLabel(sev) })}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 16px',
        borderTop: `1px solid ${c.border}`, cursor: 'pointer',
        background: isSelected ? `${color}14` : 'transparent',
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = c.rowHover; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? `${color}14` : 'transparent'; }}
    >
      {/* Condition + severity */}
      <div style={{ minWidth: 170, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: c.slate }}>{conditionLabel(row.condition?.condition_type)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
          <span style={{ fontSize: 11, fontWeight: 600, color }}>{severityLabel(sev)}</span>
        </div>
        {/* SRR alerting while the CHF survey itself still reads stable —
            without this, a clinician looking at just the CHF row would
            have no way to know the companion breathing-rate reading needs
            attention too. c.sandstone text here previously failed WCAG AA
            contrast (~2.25:1 on white, needs 4.5:1) on the single most
            clinically important new UI element in the dashboard — c.red
            passes comfortably (~7.7:1) and is already this app's
            established "needs attention" color. Given a background pill
            matching severityLabel's own treatment above it, rather than
            being the smallest, least-emphasized text in the row. */}
        {row.linkedSignificantChange && (
          <div style={{
            fontSize: 11, fontWeight: 700, color: c.red, backgroundColor: c.redLight,
            borderRadius: 6, padding: '2px 6px', marginTop: 4, display: 'inline-block',
          }}>
            {t('patientCard.significantChangeSRR')}
          </div>
        )}
      </div>
      {/* Status narrative */}
      <div style={{ flex: 1 }}>
        <StatusNarrative survey={row.lastSurvey} vnextRun={row.vnextRun} alert={row.alert} sev={sev} clinicalSummary={row.condition?.clinical_summary} recoveryThreshold={recoveryThreshold} />
        {row.alert && isInCatchUpWindow?.(row.alert.created_at) && (
          <div style={{
            fontSize: 10, fontWeight: 700, color: c.slate, backgroundColor: c.fog,
            border: `1px solid ${c.border}`, borderRadius: 10, padding: '2px 8px',
            marginTop: 4, display: 'inline-block',
          }}>
            {t('patientCard.fromBillingLapse')}
          </div>
        )}
      </div>
      {/* Mark Reviewed / Make Active Again (if any) — merged 2026-09-09
          (Bryan): Dismiss/Snooze/Acknowledge were three buttons doing the
          same underlying thing (move this out of the active queue) with
          only cosmetic differences in when it resurfaces. One button
          now: marking reviewed never resolves the alert or changes its
          color, only its queue position (see docs/ALERT_LIFECYCLE_
          REDESIGN). A reviewed alert shows a "Make Active Again" action
          instead, plus when it was reviewed and by whom. */}
      {row.alert && (
        row.alert.dismissed_at ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
            <span style={{ fontSize: 10, color: c.textSoft, whiteSpace: 'nowrap' }}>
              {t('patientCard.reviewed', { time: relDate(row.alert.dismissed_at) })}
            </span>
            <button onClick={e => { e.stopPropagation(); onReactivateAlert(row.alert.id); }}
              style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, cursor: 'pointer', color: c.textSoft, fontSize: 11, padding: '4px 8px', whiteSpace: 'nowrap' }}>
              {t('patientCard.makeActiveAgain')}
            </button>
          </div>
        ) : (
          <button onClick={e => { e.stopPropagation(); onDismissAlert(row.alert.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSoft, fontSize: 11, padding: '4px 6px', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {t('patientCard.markReviewed')}
          </button>
        )
      )}
      {/* Review */}
      <button onClick={e => { e.stopPropagation(); onSelect(row); }}
        style={{ padding: '5px 12px', border: `1px solid ${color}`, borderRadius: 6, background: 'none', color, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
        {t('patientCard.review')}
      </button>
    </div>
  );
}
